import {
  Avatar,
  Card,
  Empty,
  Input,
  Select,
  Tabs,
  Typography,
} from "antd";
import {
  ArrowRightOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  DownOutlined,
  FileTextOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";

import { ReactComponent as ConsentCard } from "../../../../Assets/consentCard.svg";
import empty from "../../../../Assets/emptyStateConsent.png";
import { translation } from "../../../../i18n/translationConstant";
import { ConsentDetail } from "../../../../Models/Response/Consent/GetAllConsent";
import {
  getConsentCounts,
  getPaginatedConsentByStatus,
} from "../../../../Services/ConsentServices/ConsentService";
import { defaultPageSize } from "../../../../Utils/Constants/apiConstants";
import {
  ConsentApprovalStatusType,
  ConsentStatusType,
} from "../../../../Utils/Constants/consentTypes";
import useINF from "../../../Common/Components/InfiniteScroll";
import TimeFromNow from "../../../Common/Components/TimeFromNow";
import { UserRespose } from "../../../../redux/Reducer/userReducer";
import { useFingerprintValidation } from "../../../Authentication/FingerprintValidationContext";

import "./index.css";
import {
  AcitivityTypes,
  QueryParams,
  Routes,
} from "../../../../Utils/Constants/routes";

const { TabPane } = Tabs;
const { Text } = Typography;
const { Option } = Select;

interface Consent {
  [key: number]: {
    numberOfConsent: number;
    consentDetails: ConsentDetail[];
    title: string;
  };
}

interface HomeProps {
  user: UserRespose;
}

const ConsentComponent: React.FC<HomeProps> = ({ user }) => {
  const { push } = useHistory();
  const { t } = useTranslation();

  const [consents, setConsents] = useState<Consent>({
    1: {
      numberOfConsent: 0,
      consentDetails: [],
      title: t(translation.lblPendingConsent),
    },
    2: {
      numberOfConsent: 0,
      consentDetails: [],
      title: t(translation.lblActiveConsent),
    },
    3: {
      numberOfConsent: 0,
      consentDetails: [],
      title: t(translation.lblOtherConsent),
    },
    4: {
      numberOfConsent: 0,
      consentDetails: [],
      title: t(translation.lblPausedConsent),
    },
    5: {
      numberOfConsent: 0,
      consentDetails: [],
      title: t(translation.lblRevokedConsent),
    },
    6: {
      numberOfConsent: 0,
      consentDetails: [],
      title: t(translation.lblRejectedConsent),
    },
    7: {
      numberOfConsent: 0,
      consentDetails: [],
      title: t(translation.lblExpiredConsent),
    },
  });

  const [page, setPage] = useState(1);
  const pageSize = defaultPageSize;

  const [activeKey, setActiveKey] = useState(1);
  const [status, setStatus] = useState([ConsentStatusType.CREATED]);
  const [hasMore, setHasMore] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [sortBy, setSortBy] = useState("recent");

  const observer = useRef<any>();
  const { newObserver, ref } = useINF(observer, hasMore, setPage);
  observer.current = newObserver.current;

  const getConsentsByStatus = useCallback(() => {
    getPaginatedConsentByStatus(page - 1, pageSize, status.toString())
      .then((res) => res.data)
      .then((res) => {
        setHasMore(!res.last);

        setConsents((old) => ({
          ...old,
          [activeKey]: {
            ...old[activeKey],
            consentDetails:
              page === 1
                ? res.content
                : old[activeKey].consentDetails.concat(res.content),
            numberOfConsent: res.totalElements,
          },
        }));

        getConsentCounts()
          .then((res1) => res1.data)
          .then((res1) => {
            setConsents((old) => ({
              ...old,
              1: {
                ...old[1],
                numberOfConsent: res1.numberOfPendingConsent,
              },
              2: {
                ...old[2],
                numberOfConsent: res1.numberOfActiveConsent,
              },
              3: {
                ...old[3],
                numberOfConsent:
                  res1.numberOfOtherConsent + res1.numberOfPausedConsent,
              },
            }));
          });
      });
  }, [pageSize, page, status, activeKey]);

  const { checkFingerprint } = useFingerprintValidation();

  useEffect(() => {
    getConsentsByStatus();
    checkFingerprint();
  }, [getConsentsByStatus, checkFingerprint]);

  const onTabChange = (key: string) => {
    const k = parseInt(key, 10);

    setPage(1);
    setHasMore(true);
    setSearchText("");

    switch (k) {
      case 1:
        setStatus([ConsentStatusType.CREATED]);
        setActiveKey(1);
        break;

      case 2:
        setStatus([ConsentStatusType.ACTIVE]);
        setActiveKey(2);
        break;

      case 3:
        setStatus([ConsentStatusType.PAUSED]);
        setActiveKey(3);
        break;

      default:
        break;
    }
  };

  const onOtherStatusChange = (value: string) => {
    setPage(1);
    setHasMore(true);

    const statusMap: { [key: string]: ConsentStatusType } = {
      paused: ConsentStatusType.PAUSED,
      revoked: ConsentStatusType.REVOKED,
      rejected: ConsentStatusType.REJECTED,
      expired: ConsentStatusType.EXPIRED,
    };

    setStatus([statusMap[value]]);
  };

  const consentCardClick = (consent: ConsentDetail) => {
    push({
      pathname: `/consent/${
        consent.consentStatusType === ConsentStatusType.CREATED
          ? ConsentApprovalStatusType.PENDING
          : consent.consentStatusType
      }/${consent.consentid ? consent.consentid : consent.consentHandle}`,
      state: {
        consentCard: consent,
      },
    });
  };

  const historyClick = (consent: ConsentDetail) => {
    push({
      pathname: Routes.more + Routes.history,
      search: `?${QueryParams.entityId}=${consent.consentHandle}&${QueryParams.entityType}=${AcitivityTypes.consent}&${QueryParams.entityType}=${AcitivityTypes.account}`,
    });
  };

  const filteredConsents = [...(consents[activeKey].consentDetails || [])]
    .filter((consent) =>
      (consent.fiuName || "")
        .toLowerCase()
        .includes(searchText.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === "oldest") {
        return (
          new Date(a.updatedOn).getTime() -
          new Date(b.updatedOn).getTime()
        );
      }

      return (
        new Date(b.updatedOn).getTime() -
        new Date(a.updatedOn).getTime()
      );
    });

  const tabTitle = (key: number) => {
    const tabLabels: { [key: number]: string } = {
      1: t(translation.lblPendingConsent),
      2: t(translation.lblActiveConsent),
      3: t(translation.lblOtherConsent),
    };

    return (
      <span
        className={
          activeKey === key
            ? "homeTabTitle active"
            : "homeTabTitle"
        }
      >
        {tabLabels[key]} ({consents[key].numberOfConsent})
      </span>
    );
  };

  const renderConsent = () => {
    if (!filteredConsents.length) {
      return (
        <Empty
          image={empty}
          imageStyle={{
            width: 120,
            height: 100,
            marginTop: 40,
          }}
          description={
            <div className="emptyConsentText">
              <Text strong>
                {t(translation.errorConsentNotFound)}
              </Text>

              <Text>
                {searchText
                  ? t(translation.lblHomeSearch)
                  : t("consentsErrorMeassage", {
                      type: t(status.toString().toLowerCase()),
                    })}
              </Text>
            </div>
          }
        />
      );
    }

    return (
      <>
        {filteredConsents.map(
          (consent: ConsentDetail, index: number) => (
            <div
              className="consentRow"
              key={
                consent.consentid ||
                consent.consentHandle ||
                index
              }
            >
              <div className="consentRowMain">
                <span className="consentIndex">
                  {index + 1}.
                </span>

                <Avatar
                  className="consentAvatar"
                  src={consent.logoUrl}
                  icon={<FileTextOutlined />}
                />

                <div className="consentRowText">
                  <Text className="consentFiuName">
                    {consent.fiuName}
                  </Text>

                  <Text className="consentAccessText">
                    {t(
                      translation.lblWantsToAccessAccount
                    )}
                  </Text>
                </div>

                {/* <span className="consentCategory">
                  {t(translation.lblAccountDetails)}
                </span> */}
              </div>

              <div className="consentRowActions">
                <span className="consentTime">
                  <ClockCircleOutlined />

                  <TimeFromNow
                    className="consentHomeTime"
                    date={consent.updatedOn}
                  />
                </span>

                <button
                  type="button"
                  className="primaryActionButton"
                  onClick={() => consentCardClick(consent)}
                >
                  {activeKey === 1
                    ? t(translation.lblReviewConsent)
                    : t(translation.lblViewDetails)}
                </button>

                <button
                  type="button"
                  className="historyActionButton"
                  onClick={() => historyClick(consent)}
                >
                  <ClockCircleOutlined />
                  <span>{t(translation.lblViewDetails)}</span>
                </button>

                <ArrowRightOutlined className="rowArrow" />
              </div>
            </div>
          )
        )}

        <div className="infiniteDiv" ref={ref} />
      </>
    );
  };

  return (
    <div className="consentLayout">
      <div className="consentHeader">
        {/* Pending */}
        <Card
          className="summaryCard pendingSummary"
          onClick={() => onTabChange("1")}
        >
          <div className="summaryIcon">
            <span>⌛</span>
          </div>

          <div className="summaryContent">
            <Text>
              {t(translation.lblPendingConsent)}
            </Text>
            <strong>
              {consents[1].numberOfConsent}
            </strong>
            <small>
              {t(translation.lblActionRequired)}
            </small>
          </div>

          <ArrowRightOutlined className="summaryArrow" />
        </Card>

        {/* Active */}
        <Card
          className="summaryCard activeSummary"
          onClick={() => onTabChange("2")}
        >
          <div className="summaryIcon">
            <CheckCircleFilled />
          </div>

          <div className="summaryContent">
            <Text>
              {t(translation.lblActiveConsent)}
            </Text>
            <strong>
              {consents[2].numberOfConsent}
            </strong>
            <small>
              {t(translation.lblCurrentlyInUse)}
            </small>
          </div>

          <ArrowRightOutlined className="summaryArrow" />
        </Card>

        {/* Other */}
        <Card
          className="summaryCard otherSummary"
          onClick={() => onTabChange("3")}
        >
          <div className="summaryIcon">
            <FileTextOutlined />
          </div>

          <div className="summaryContent">
            <Text>
              {t(translation.lblPastConsent)}
            </Text>
            <strong>
              {consents[3].numberOfConsent}
            </strong>
            <small>
              {t(translation.lblExpiredRevokedOrClosed)}
            </small>
          </div>

          <ArrowRightOutlined className="summaryArrow" />
        </Card>

        {/* Information */}
        <Card className="financialGoalCard">
          <div className="financialGoalText">
            <strong>
              {t(translation.lblYourData)}
              <br />
              {t(translation.lblYourControl)}
            </strong>

            <span>
              {t(translation.lblManageFinancialData)}
            </span>

            <button
              type="button"
              onClick={() =>
                window.open(
                  "https://www.ink-aa.com/data-sharing-consent.html",
                  "_blank"
                )
              }
            >
              {t(translation.lblLetsGetStarted)} →
            </button>
          </div>

          <ConsentCard className="financialGoalImage" />
        </Card>
      </div>

      <Card
        bordered={false}
        className="consentDetailsHome"
      >
        <Tabs
          activeKey={activeKey.toString()}
          onChange={onTabChange}
          className="consentTabs"
        >
          <TabPane
            tab={tabTitle(1)}
            key="1"
          />

          <TabPane
            tab={tabTitle(2)}
            key="2"
          />

          <TabPane
            tab={tabTitle(3)}
            key="3"
          />
        </Tabs>

        <div className="consentFilters">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder={t(translation.lblHomeSearch)}
            value={searchText}
            onChange={(e) =>
              setSearchText(e.target.value)
            }
            className="consentSearch"
          />

          <Select
            value={sortBy}
            onChange={setSortBy}
            className="filterSelect sortSelect"
            suffixIcon={<DownOutlined />}
          >
            <Option value="recent">
              {t(translation.lblSortByRecent)}
            </Option>

            <Option value="oldest">
              {t(translation.lblSortByOldest)}
            </Option>
          </Select>
        </div>

        {activeKey === 3 && (
          <div className="otherStatusFilter">
            <Select
              defaultValue="paused"
              onChange={onOtherStatusChange}
            >
              <Option value="paused">
                {t(translation.lblPausedConsent)}
              </Option>

              <Option value="revoked">
                {t(translation.lblRevokedConsent)}
              </Option>

              <Option value="rejected">
                {t(translation.lblRejectedConsent)}
              </Option>

              <Option value="expired">
                {t(translation.lblExpiredConsent)}
              </Option>
            </Select>
          </div>
        )}

        <div className="consentList">
          {renderConsent()}
        </div>
      </Card>

      {/* <div className="homeSupportBar">
        <span>
          💡 Need help? Check our{" "}
          <u>FAQs</u> or{" "}
          <u>contact support</u>.
        </span>

        <span>
          💬 We value your feedback{" "}
          <ArrowRightOutlined />
        </span>
      </div> */}
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  user: state.user.user,
});

export default connect(mapStateToProps)(
  ConsentComponent
);